/**
 * tm/pages/MobileFind.tsx — 휴대폰 「찾기」 · 업체 한눈에 · 팀장님 한눈에 (2026-10-12)
 *  지도사 「팀장님이나 회사에서 전화가 오면 그 팀장님 또는 그 회사 현황 · 상담 이력 · 메모 · 진행 업무를 바로 열어 볼 수 있어야」
 *  UX 패널(인지공학 · 운전 HMI · 토스형 모바일) 반영:
 *   - 이름 두 글자 또는 전화번호 뒷자리(숫자만 치면 끝자리가 맞는 것 먼저) · 한 글자마다 바로 · 숫자 자판 바로 바꾸기 · 붙여 넣기
 *   - 글자 없이 고르기: 오늘 만날 상대 → 최근 본 것 순 칩 5개
 *   - 결과 화면: 맨 위 요약 한 줄 고정 → 마지막 메모 → 진행 업무(마감 가까운 순 · 색점) → 상담 이력(다음 → 지난) → (팀장님) 업체 → 메모 전체
 *   - 아래 「메모 남기기」 하나만 고정(통화 중 바로 적기) · 전화 버튼 없음(번호는 글자로만)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import * as api from '../api';
import type { Comment, Company, Consultation, FP, Task } from '../types';
import { inConsult } from '../types';
import { companyWmIds, wmNames } from '../wmList';
import { G, Icon, clamp } from '../portal-ui/tds';
import { d10, kstToday, md, rowOf } from '../flow/flowLogic';
import type { FlowRow } from '../flow/flowLogic';
import { short } from '../portal-ui/portalLogic';
import { Card, CardHead, Chips, NAVY, ROW, SaveBar, TOSS, Tint } from './mobileKit';
import { useFlow } from '../flow/FlowActions';
import MobileTaskSheet from './MobileTaskSheet';
import MobileCallList from './MobileCallList';

export type HitKind = 'co' | 'fp';
export interface Hit { kind: HitKind; id: string; name: string; sub: string; hl?: string; owner?: string }
export const KIND_ICON: Record<HitKind, string> = { co: 'bld', fp: 'user' };

const digits = (s: string | null | undefined) => String(s || '').replace(/\D/g, '');
const CHO = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
/** 초성 — 「ㅅㅈㅎ」로 「손주희」 찾기 */
export function chosung(s: string): string {
  return Array.from(String(s || '')).map(ch => { const c = ch.charCodeAt(0) - 0xAC00; return c >= 0 && c < 11172 ? CHO[Math.floor(c / 588)] : ch; }).join('');
}
const isCho = (s: string) => /^[ㄱ-ㅎ\s]+$/.test(s);
const flat = (s: string | null | undefined) => String(s || '').toLowerCase().replace(/\s/g, '');
const tail4 = (s: string | null | undefined) => { const d = digits(s); return d.length >= 4 ? d.slice(-4) : ''; };

export const fpLabel = (f: FP) => `${f.name} ${f.title || '팀장'}`.trim();
export const fpOrg = (f: FP) => (f.company || '').trim();

/** 찾기 — 숫자만 치면 전화번호(끝자리 맞음 > 들어 있음) · 글자는 이름(앞 맞음 > 들어 있음) > 소속 · 대표 · 담당자 */
export function findHits(q: string, companies: Company[], fps: FP[], compSub: (c: Company) => string): Hit[] {
  const k = q.trim();
  if (!k) return [];
  const num = /^[\d\s()+-]+$/.test(k) ? digits(k) : '';
  if (num && num.length < 3) return [];
  const lk = flat(k);
  const byPhone = (phones: (string | null | undefined)[]) => {
    let s = 0;
    phones.map(digits).filter(Boolean).forEach(p => { if (p.endsWith(num)) s = Math.max(s, 3); else if (p.includes(num)) s = Math.max(s, 1); });
    return s;
  };
  const cho = isCho(k);
  const byText = (name: string, others: (string | null | undefined)[]) => {
    if (cho) { const c = flat(chosung(name)); return c.startsWith(lk) ? 3 : c.includes(lk) ? 2 : others.some(o => flat(chosung(String(o || ''))).includes(lk)) ? 1 : 0; }
    const n = flat(name);
    if (n.startsWith(lk)) return 3;
    if (n.includes(lk)) return 2;
    return others.some(o => flat(o).includes(lk)) ? 1 : 0;
  };
  const out: { h: Hit; s: number }[] = [];
  fps.forEach(f => {
    const s = num ? byPhone([f.phone]) : byText(f.name, [f.company]);
    if (s) out.push({ s: s + 0.1, h: { kind: 'fp', id: f.id, name: fpLabel(f), sub: [num ? `팀장님 …${tail4(f.phone)}` : '', fpOrg(f)].filter(Boolean).join(' · '), hl: num } });
  });
  companies.forEach(c => {
    const s = num ? byPhone([c.ceo_phone, c.real_ceo_phone, c.manager_phone]) : byText(c.name, [c.ceo, c.real_ceo, c.manager]);   // 실제 대표도(2026-10-18)
    // 번호로 찾으면 누구 번호인지(대표 · 담당) 먼저 — 받는 중 망설이지 않게
    // 번호로 찾으면 「누구 번호인지」가 제목(김OO 대표 …2002) · 업체는 둘째 줄(검토: 답이 회색 둘째 줄에 묻힘)
    const own = !num ? '' : digits(c.ceo_phone).includes(num) ? `${c.ceo || '대표'} 대표 …${tail4(c.ceo_phone)}` : digits(c.real_ceo_phone).includes(num) ? `${c.real_ceo || '실제'} 실제 대표 …${tail4(c.real_ceo_phone)}` : digits(c.manager_phone).includes(num) ? `${c.manager || '담당'} 담당 …${tail4(c.manager_phone)}` : '';
    if (s) out.push({ s, h: { kind: 'co', id: c.id, name: c.name, sub: own ? [c.name, compSub(c)].filter(Boolean).join(' · ') : compSub(c), hl: num, owner: own.trim() || undefined } });
  });
  return out.sort((a, b) => b.s - a.s || a.h.name.localeCompare(b.h.name, 'ko')).slice(0, 20).map(x => x.h);
}

/* ── 최근 본 것(이 휴대폰) ── */
const RECENT = 'roum-m-recent';
interface Seen { kind: HitKind; id: string }
function readSeen(): Seen[] { try { const v = JSON.parse(localStorage.getItem(RECENT) || '[]') as Seen[]; return Array.isArray(v) ? v : []; } catch { return []; } }
export function remember(kind: HitKind, id: string) {
  try { localStorage.setItem(RECENT, JSON.stringify([{ kind, id }, ...readSeen().filter(x => !(x.kind === kind && x.id === id))].slice(0, 12))); } catch { /* 보관 못 함 */ }
}

/** 칩 — 오늘 만날 상대(상담 업체 · 팀장님) 먼저, 그다음 최근 본 순 */
export function quickPicks(companies: Company[], fps: FP[], consultations: Consultation[], n = 5, withToday = true): (Hit & { today: boolean })[] {
  const t = kstToday();
  const coBy = new Map(companies.map(c => [c.id, c]));
  const fpBy = new Map(fps.map(f => [f.id, f]));
  const out: (Hit & { today: boolean })[] = [];
  const add = (kind: HitKind, id: string | null | undefined, today: boolean) => {
    if (!id || out.some(x => x.kind === kind && x.id === id)) return;
    if (kind === 'co') { const c = coBy.get(id); if (c) out.push({ kind, id, name: c.name, sub: '', today }); }
    else { const f = fpBy.get(id); if (f) out.push({ kind, id, name: f.name, sub: '', today }); }
  };
  if (withToday) consultations.filter(c => d10(c.date) === t && c.confirm_status !== 'tentative').sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')))
    .forEach(c => { add('co', c.company_id, true); add('fp', c.fp_id, true); });
  readSeen().forEach(s => add(s.kind, s.id, false));
  return out.slice(0, n);
}

function Dot({ kind, size = 40 }: { kind: HitKind; size?: number }) {
  return <Tint icon={KIND_ICON[kind]} size={size} />;   // 토스식 — 색 하나 · 업체(건물) · 팀장님(사람) 모양으로 구분
}

/** 칩 줄 — 첫 화면 · 찾기 화면 공용 (이름 6자 넘으면 …) */
export function PickChips({ picks, onOpen }: { picks: (Hit & { today: boolean })[]; onOpen: (h: Hit) => void }) {
  if (!picks.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
      {picks.map(p => (
        <button key={p.kind + p.id} type="button" className="tds-btn" onClick={() => onOpen(p)} aria-label={`${p.name} 열기`}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 44, padding: '0 14px 0 6px', borderRadius: 22, border: `1px solid ${p.today ? TOSS.blue : TOSS.line}`, background: '#fff', color: TOSS.text, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          <Dot kind={p.kind} size={30} />{p.today && <span style={{ color: TOSS.blue, fontWeight: 700 }}>오늘</span>}{short(p.name, 7)}
        </button>
      ))}
    </div>
  );
}

/** 첫 화면 찾기 칸 — 입력 칸 모양(누르면 찾기 화면 · 자판이 첫 화면 타일을 가리지 않게) */
export function FindBar({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" className="tds-btn" onClick={onOpen} aria-label="팀장님 · 업체 찾기"
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 56, padding: '0 16px', borderRadius: 16, border: `1px solid ${TOSS.line}`, background: '#fff', color: TOSS.sub, fontSize: 17, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
      <Icon name="search" size={22} color={TOSS.sub} />
      <span style={{ flex: 1, minWidth: 0, ...clamp(1) }}>팀장님 · 업체 · 번호 뒷자리</span>
    </button>
  );
}

/** 찾기 화면 */
export function FindView({ companies, fps, consultations, compSub, onOpen, goNewCompany, goNewFp }: { companies: Company[]; fps: FP[]; consultations: Consultation[]; compSub: (c: Company) => string; onOpen: (h: Hit) => void; goNewCompany: () => void; goNewFp: () => void }) {
  const [q, setQ] = useState('');
  const [numPad, setNumPad] = useState(true);   // 전화 올 때 = 번호 뒷자리가 먼저(패널) · 이름은 「가나다」
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  const hits = useMemo(() => findHits(q, companies, fps, compSub), [q, companies, fps, compSub]);
  const picks = useMemo(() => quickPicks(companies, fps, consultations, 8), [companies, fps, consultations]);
  const swap = () => { setNumPad(v => !v); if (ref.current) { ref.current.blur(); window.setTimeout(() => { if (ref.current) ref.current.focus(); }, 60); } };
  const paste = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) setQ(t.trim().slice(0, 40)); } catch { if (ref.current) ref.current.focus(); }
  };
  const k = q.trim();
  const tooShort = /^[\d\s()+-]+$/.test(k) && digits(k).length < 3;
  const side: CSSProperties = { flexShrink: 0, minWidth: 72, height: 56, padding: '0 12px', borderRadius: 16, border: `1px solid ${TOSS.line}`, background: '#fff', color: TOSS.blue, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <label className="tds-in" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, height: 56, padding: '0 12px 0 16px', borderRadius: 16, border: `1px solid ${TOSS.line}`, background: '#fff', boxSizing: 'border-box' }}>
          <Icon name="search" size={20} color={TOSS.sub} />
          <input ref={ref} value={q} onChange={e => setQ(e.target.value)} inputMode={numPad ? 'numeric' : 'search'} enterKeyHint="search" autoComplete="off"
            placeholder={numPad ? '번호 뒷자리 4개' : '이름 · 초성(ㅅㅈㅎ)'} aria-label="팀장님 · 업체 찾기"
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 18, background: 'transparent', fontFamily: 'inherit', color: G.g900 }} />
          {q && <button type="button" onClick={() => { setQ(''); if (ref.current) ref.current.focus(); }} aria-label="지우기" style={{ width: 36, height: 36, border: 'none', borderRadius: '50%', background: G.g200, color: G.g700, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>×</button>}
        </label>
        <button type="button" className="tds-btn" onClick={swap} style={side} aria-label={numPad ? '글자 자판으로' : '숫자 자판으로'}>{numPad ? '글자로' : '숫자로'}</button>
      </div>
      <button type="button" className="tds-btn" onClick={() => void paste()} style={{ marginTop: 8, width: '100%', height: 48, borderRadius: 14, border: 'none', background: TOSS.blueLt, color: TOSS.blueDk, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
        복사한 번호 붙여 넣기
      </button>

      {!k && picks.length > 0 && (
        <div style={{ marginTop: 12 }}><Card pad="4px 0">
          <CardHead title="오늘 만날 분 · 최근" />
          {picks.map(p => <HitRow key={p.kind + p.id} h={{ ...p, sub: `${p.kind === 'co' ? '업체' : '팀장님'} · ${p.today ? '오늘 상담' : '최근 봄'}` }} onOpen={onOpen} />)}
        </Card></div>
      )}
      {k && tooShort && <div style={{ padding: '20px 4px', fontSize: 16, color: G.g700 }}>숫자는 3자리부터 찾아요</div>}
      {k && !tooShort && hits.length === 0 && (
        <div style={{ padding: '20px 4px' }}>
          <div style={{ fontSize: 16, color: G.g700 }}>「{short(k, 12)}」(으)로 찾은 분이 없어요</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8, marginTop: 12 }}>
            <button type="button" className="tds-btn" onClick={goNewCompany} style={side}>업체 등록</button>
            <button type="button" className="tds-btn" onClick={goNewFp} style={side}>팀장님 등록</button>
          </div>
        </div>
      )}
      {hits.length > 0 && <div style={{ marginTop: 12 }}><Card pad="4px 0">{hits.map(h => <HitRow key={h.kind + h.id} h={h} onOpen={onOpen} />)}</Card></div>}
    </div>
  );
}

/** 맞은 숫자(뒷자리) 굵게 */
function mark(text: string, hl?: string): ReactNode {
  if (!hl || hl.length < 3) return text;
  const tail = hl.slice(-4);
  const i = text.indexOf(tail);
  return i < 0 ? text : <>{text.slice(0, i)}<b style={{ color: G.g900 }}>{tail}</b>{text.slice(i + tail.length)}</>;
}

function HitRow({ h, onOpen }: { h: Hit; onOpen: (h: Hit) => void }) {
  return (
    <button type="button" className="tds-row" onClick={() => onOpen(h)}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 72, padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
      <Dot kind={h.kind} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text, fontVariantNumeric: 'tabular-nums', ...clamp(1) }}>{h.owner ? mark(h.owner, h.hl) : h.name}</span>
        {h.sub && <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, marginTop: 2, ...clamp(1) }}>{h.owner ? h.sub : mark(h.sub, h.hl)}</span>}
      </span>
      <Icon name="r" size={18} color={G.g500} sw={2} />
    </button>
  );
}

/* ── 한눈에 보기 공용 ── */
interface TaskState { text: string; color: string; rank: number; soon: boolean; over: boolean }
/** 진행 업무 한 줄 상태(토스식 · 검토 5인) — 빨강은 「지남」만 · 오늘 · 3일 안은 파랑 · 회신 대기(상대 차례) · 남음은 회색 글자 */
export function taskState(r: FlowRow): TaskState {
  if (r.kind === 'wait') return { text: `회신 대기 ${r.waitDays}일`, color: r.waitDays > 14 ? '#E8590C' : TOSS.sub, rank: 2000 - r.waitDays, soon: false, over: false };
  if (r.d === null) return { text: '진행 중', color: TOSS.sub, rank: 5000, soon: false, over: false };
  if (r.d < 0) return { text: `${-r.d}일 지남`, color: TOSS.red, rank: r.d, soon: false, over: true };
  if (r.d === 0) return { text: '오늘 마감', color: TOSS.blue, rank: 0, soon: true, over: false };
  return { text: `${r.d}일 남음`, color: r.d <= 3 ? TOSS.blue : TOSS.sub, rank: r.d, soon: r.d <= 3, over: false };
}
function activeRows(tasks: Task[], companies: Company[]): { r: FlowRow; st: TaskState }[] {
  const today = kstToday();
  const by = new Map(companies.map(c => [c.id, c]));
  const out: { r: FlowRow; st: TaskState }[] = [];
  tasks.forEach(t => { const c = by.get(t.company_id); if (!c) return; const r = rowOf(t, c, today); if (r.kind !== 'off') out.push({ r, st: taskState(r) }); });
  return out.sort((a, b) => a.st.rank - b.st.rank);
}

const CONSULT_ST: Record<string, string> = { scheduled: '예정', completed: '완료', sent: '완료' };
function consultBuckets(list: Consultation[]): { next: Consultation[]; past: Consultation[] } {
  const t = kstToday();
  const key = (c: Consultation) => `${d10(c.date)} ${c.time || ''}`;
  const next = list.filter(c => d10(c.date) >= t && c.status === 'scheduled').sort((a, b) => key(a).localeCompare(key(b)));
  const past = list.filter(c => !next.includes(c)).sort((a, b) => key(b).localeCompare(key(a)));
  return { next, past };
}

/** 묶음 하나 = 흰 카드(토스식) */
function Sec({ title, count, right, children }: { title: string; count?: number; right?: ReactNode; children: ReactNode }) {
  return (
    <Card pad="4px 0 6px">
      <CardHead title={title} count={count} right={right} />
      <div style={{ padding: '0 20px' }}>{children}</div>
    </Card>
  );
}
function Empty({ children }: { children: ReactNode }) { return <div style={ROW.empty}>{children}</div>; }
function LinkBtn({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return <button type="button" className="tds-btn" onClick={onClick} style={{ minHeight: 44, padding: '0 4px', border: 'none', background: 'none', color: NAVY, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>{children}</button>;
}

/** 머리 숫자 세 칸(토스식) — 지남(빨강 · 0이면 회색) · 진행 · 다음 상담/동행(오늘이면 시간) */
/** 머리 숫자 4칸(업체 · 팀장님 화면 같은 틀 · 같은 폭) — 기한 지남(빨강) · 진행 · 셋째 칸 · 다음 상담(오늘이면 시간) */
function headLine(third: [string, number], rows: { st: TaskState }[], next: Consultation | undefined): ReactNode {
  const over = rows.filter(x => x.st.over).length;
  const t = kstToday();
  // 칸이 좁아 「오늘 23:30」이 잘림 → 오늘이면 칸 이름 「오늘 상담」 + 시간만 · 다른 날은 「10/15」
  const isToday = !!next && d10(next.date) === t;
  const when = next ? (isToday ? (next.time ? String(next.time).slice(0, 5) : '시간 미정') : md(next.date).replace(/\(.\)$/, '')) : '없음';
  const cell = (label: string, v: ReactNode, color: string) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, color: TOSS.sub, ...clamp(1) }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, marginTop: 2, fontVariantNumeric: 'tabular-nums', ...clamp(1) }}>{v}</div>
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8 }}>
      {cell('기한 지남', `${over}건`, over ? TOSS.red : TOSS.faint)}
      {cell('진행', `${rows.length}건`, TOSS.text)}
      {cell(third[0], `${third[1]}${third[0] === '업체' ? '곳' : '건'}`, third[1] ? TOSS.text : TOSS.faint)}
      {cell(isToday ? '오늘 상담' : '다음 상담', when, next ? TOSS.blue : TOSS.faint)}
    </div>
  );
}

/** 맨 위 요약(스크롤해도 고정) */
function Summary({ kind, title, chip, line }: { kind: HitKind; title: string; chip?: string; line: ReactNode }) {
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 4, margin: '0 -16px 12px', padding: '8px 36px 14px', background: '#fff', borderBottom: `1px solid ${TOSS.line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Dot kind={kind} size={36} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 22, fontWeight: 800, color: TOSS.text, ...clamp(1) }}>{title}</span>
        {chip && <span style={{ flexShrink: 0, maxWidth: '40%', padding: '4px 10px', borderRadius: 10, background: TOSS.bg, color: TOSS.sub, fontSize: 15, fontWeight: 700, ...clamp(1) }}>{chip}</span>}
      </div>
      <div style={{ marginTop: 12 }}>{line}</div>
    </div>
  );
}

/** onCompany = 업체 화면으로(여러 업체가 섞인 목록) · onTask = 그 업무 처리 판(업체 화면 · 2026-10-28 「업무 종료 · 단계 이동」) */
function TaskRows({ rows, showCompany, onCompany, onTask }: { rows: { r: FlowRow; st: TaskState }[]; showCompany?: boolean; onCompany?: (id: string) => void; onTask?: (t: Task) => void }) {
  return (
    <>
      {rows.map(({ r, st }) => {
        const tap = onTask ? () => onTask(r.task) : onCompany ? () => onCompany(r.comp.id) : null;
        return (
        <div key={r.task.id} role={tap ? 'button' : undefined} tabIndex={tap ? 0 : undefined} className={tap ? 'tds-row' : undefined}
          aria-label={onTask ? `${r.task.type} 처리하기` : undefined}
          onClick={tap || undefined} onKeyDown={tap ? e => { if (e.key === 'Enter') tap(); } : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 64, padding: '8px 0', borderBottom: `1px solid ${G.g100}`, cursor: tap ? 'pointer' : 'default' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {/* 여러 업체가 섞이면 업체 이름이 제목(한 줄 다 씀) · 업무 · 단계는 둘째 줄 */}
            <span style={{ ...ROW.title, ...clamp(1) }}>{showCompany ? r.comp.name : r.task.type}</span>
            <span style={{ ...ROW.sub, ...clamp(1) }}>{[showCompany ? r.task.type : '', r.step ? r.step.name : r.task.status].filter(Boolean).join(' · ')}</span>
          </span>
          <span style={{ ...ROW.state, color: st.color, fontVariantNumeric: 'tabular-nums' }}>{st.text}</span>
          {tap && <Icon name="r" size={18} color={G.g400} sw={2} />}
        </div>
        );
      })}
    </>
  );
}

function ConsultRows({ list, who, onCompany }: { list: Consultation[]; who: 'fp' | 'co'; onCompany?: (id: string) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const t = kstToday();
  return (
    <>
      {list.map(c => {
        const on = open === c.id;
        const body = [c.content, c.notes].map(x => String(x || '').trim()).filter(Boolean).join('\n');
        const st = c.confirm_status === 'tentative' ? '날짜 조율 중' : c.status === 'scheduled' && d10(c.date) < t ? '결과 미입력' : CONSULT_ST[c.status] || '';
        const title = who === 'co' ? (c.topic || '동행상담') : (c.company_name || '업체');
        // 다른 줄과 같은 틀 — 제목(무엇) · 둘째 줄 언제 · 누구 · 어디(2026-10-28 날짜 칸을 따로 두던 것 → 한 틀)
        const sub = [`${md(c.date)} ${c.time ? String(c.time).slice(0, 5) : '시간 미정'}`, who === 'co' ? (c.fp_name ? `${c.fp_name} 팀장님` : '') : (c.topic || ''), c.location].filter(Boolean).join(' · ');
        return (
          <div key={c.id} style={{ borderBottom: `1px solid ${G.g100}` }}>
            <button type="button" className="tds-row" onClick={() => setOpen(on ? null : c.id)} aria-expanded={on}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: ROW.minHeight, padding: '8px 0', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...ROW.title, ...clamp(1) }}>{title}</span>
                <span style={{ ...ROW.sub, ...clamp(1) }}>{sub}</span>
              </span>
              <span style={{ ...ROW.state, color: st === '결과 미입력' ? '#C2410C' : TOSS.sub }}>{st}</span>
            </button>
            {on && (
              <div style={{ padding: '0 0 12px' }}>
                {body ? <div style={{ ...ROW.body, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body}</div> : <div style={{ ...ROW.body, color: TOSS.sub }}>적은 내용이 없어요</div>}
                {who === 'fp' && onCompany && <div style={{ marginTop: 6 }}><LinkBtn onClick={() => onCompany(c.company_id)}>이 업체 보기 ›</LinkBtn></div>}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

type Memo = { id: string; at: string; text: string; who: string };
function MemoRows({ list }: { list: Memo[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      {list.map(m => {
        const on = open === m.id;
        return (
          <button key={m.id} type="button" className="tds-row" onClick={() => setOpen(on ? null : m.id)} aria-expanded={on}
            style={{ display: 'block', width: '100%', padding: '10px 0', border: 'none', borderBottom: `1px solid ${G.g100}`, background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <span style={{ ...ROW.sub, ...clamp(1) }}>{md(m.at)}{m.who ? ` · ${m.who}` : ''}</span>
            <span style={{ display: 'block', ...ROW.body, color: G.g900, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...(on ? {} : clamp(3)) }}>{m.text}</span>
          </button>
        );
      })}
    </>
  );
}
const kstIso = (iso: string) => { const ms = new Date(iso).getTime(); return isNaN(ms) ? '' : new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10); };
const toMemo = (c: Comment, who = ''): Memo => ({ id: c.id, at: kstIso(c.created_at), text: String(c.content || '').trim(), who });

function useMore(n: number): [number, (total: number) => ReactNode | null] {
  const [lim, setLim] = useState(n);
  const btn = (total: number) => (total > lim ? <LinkBtn onClick={() => setLim(l => l + 10)}>{total - lim}개 더 보기</LinkBtn> : null);
  return [lim, btn];
}

/** 업체 한눈에 */
export function CompanyView({ c, fps, wms, tasks, consultations, onFp, goMemo, goTask, goConsult, goCall, refreshTasks, toast }: {
  c: Company; fps: FP[]; wms: { id: string; name: string }[]; tasks: Task[]; consultations: Consultation[];
  onFp: (id: string) => void; goMemo: () => void; goTask: () => void; goConsult: () => void;
  /** 통화 녹음 올리기(이 업체 · 2026-10-28) */
  goCall: () => void;
  /** 업무를 바꾼 뒤 다시 불러오기 · 못 바꿨을 때 알림(2026-10-28 업무 처리 판) */
  refreshTasks: () => Promise<void>; toast: (m: string) => void;
}) {
  // 진행 업무 한 줄 → 처리 판(완료 · 미루기 · 단계 이동 · 업무 끝내기) · 되돌리기는 아래 「메모 남기기」 버튼 위로
  const { run, undoNode } = useFlow(refreshTasks, toast, 128);
  const [sheetId, setSheetId] = useState('');
  const [memos, setMemos] = useState<Memo[] | null>(null);
  const [memoErr, setMemoErr] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setMemos(null); setMemoErr(false);
    api.getComments(c.id).then(r => { if (!live) return; if (!r.success) throw new Error('x'); setMemos((r.data || []).map(x => toMemo(x))); })
      .catch(() => { if (live) { setMemos([]); setMemoErr(true); } });
    return () => { live = false; };
  }, [c.id, tick]);
  const [copied, setCopied] = useState('');
  const copy = (num: string) => {
    const done = () => { setCopied(num); window.setTimeout(() => setCopied(x => (x === num ? '' : x)), 2000); };
    if (navigator.clipboard) navigator.clipboard.writeText(num).then(done).catch(() => undefined);
  };
  const myFps = fps.filter(f => (c.fps || []).some(x => x.fp_id === f.id));
  const rows = useMemo(() => activeRows(tasks.filter(t => t.company_id === c.id), [c]), [tasks, c]);
  const sheetTask = sheetId ? tasks.find(t => t.id === sheetId) : undefined;   // 늘 최신 업무로(다시 불러온 뒤에도)
  const { next, past } = useMemo(() => consultBuckets(consultations.filter(x => inConsult(x, c.id))), [consultations, c.id]);
  const [pLim, pMore] = useMore(3);
  const [mLim, mMore] = useMore(5);
  const wmLine = wmNames(companyWmIds(c), wms, (c.wms || []).map(w => w.name));   // 담당 WM 여러 명(공동관리 · 2026-10-26)
  const line = headLine(['회신 대기', rows.filter(x => x.r.kind === 'wait').length], rows, next[0]);
  // 사람 · 번호(전화 받는 중 처음 볼 것 — 머리줄 바로 아래) · 번호는 글자로만(전화 버튼 없음) + 복사
  const people = [
    c.ceo ? { who: `대표 ${c.ceo}`, num: c.ceo_phone || '' } : null,
    c.real_ceo || c.real_ceo_phone ? { who: `실제 대표 ${c.real_ceo || ''}`.trim(), num: c.real_ceo_phone || '' } : null,
    c.manager ? { who: `담당 ${c.manager}`, num: c.manager_phone || '' } : null,
  ].filter((x): x is { who: string; num: string } => !!x);
  const info = [[c.region, c.biz_type].filter(Boolean).join(' · '), wmLine ? `WM ${wmLine}` : ''].filter(Boolean);
  return (
    <div>
      <Summary kind="co" title={c.name} chip={c.tm_status} line={line} />
      {myFps.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
          {myFps.map(f => (
            <button key={f.id} type="button" className="tds-btn" onClick={() => onFp(f.id)}
              style={{ maxWidth: '100%', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 12px 0 6px', borderRadius: 22, border: `1px solid ${G.g300}`, background: '#fff', fontSize: 15, fontWeight: 600, color: G.g900, cursor: 'pointer', fontFamily: 'inherit' }}>
              <Dot kind="fp" size={30} /><span style={{ minWidth: 0, ...clamp(1) }}>{fpLabel(f)}{fpOrg(f) ? ` · ${fpOrg(f)}` : ''}</span><Icon name="r" size={16} color={G.g500} sw={2} />
            </button>
          ))}
        </div>
      )}
      {(people.length > 0 || c.tm_memo) && (
        <div style={{ margin: '0 0 12px', padding: '10px 20px', borderRadius: 20, background: '#fff', border: `1px solid ${TOSS.line}` }}>
          {people.map(x => (
            <div key={x.who} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, fontSize: 17, color: TOSS.text, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ fontWeight: 700, flexShrink: 0 }}>{x.who}</span>
              <span style={{ flex: 1, minWidth: 0, color: G.g800, ...clamp(1) }}>{x.num}</span>
              {x.num && <button type="button" className="tds-btn" onClick={() => copy(x.num)} style={{ flexShrink: 0, height: 44, minWidth: 64, padding: '0 12px', borderRadius: 12, border: 'none', background: TOSS.blueLt, color: TOSS.blueDk, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{copied === x.num ? '복사됨' : '복사'}</button>}
            </div>
          ))}
          {c.tm_memo && <div style={{ fontSize: 15, color: '#92400E', fontWeight: 500, lineHeight: 1.55, marginTop: people.length ? 4 : 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...clamp(3) }}>{c.tm_memo}</div>}
        </div>
      )}

      <Sec title="마지막 메모">
        {memos === null ? <Empty>불러오는 중이에요</Empty> : memoErr ? <Empty>메모를 못 불러왔어요 <LinkBtn onClick={() => setTick(x => x + 1)}>다시 불러오기</LinkBtn></Empty> : memos.length ? <MemoRows list={memos.slice(0, 1)} /> : <Empty>남긴 메모가 없어요</Empty>}
      </Sec>
      <Sec title="진행 업무" count={rows.length} right={<LinkBtn onClick={goTask}>＋ 업무 추가</LinkBtn>}>
        {rows.length ? <TaskRows rows={rows} onTask={t => setSheetId(t.id)} /> : <Empty>진행 중인 업무가 없어요</Empty>}
      </Sec>
      <Sec title="상담 이력" count={next.length + past.length} right={<LinkBtn onClick={goConsult}>＋ 상담 등록</LinkBtn>}>
        {next.length + past.length === 0 && <Empty>상담 기록이 없어요</Empty>}
        <ConsultRows list={[...next, ...past.slice(0, pLim)]} who="co" />
        {pMore(past.length)}
      </Sec>
      <MobileCallList companyId={c.id} companyName={c.name} goCall={goCall} toast={toast} link={(t, on) => <LinkBtn onClick={on}>{t}</LinkBtn>} />
      {memos && memos.length > 1 && (
        <Sec title="이전 메모" count={memos.length - 1}>
          <MemoRows list={memos.slice(1, 1 + mLim)} />
          {mMore(memos.length - 1)}
        </Sec>
      )}
      {info.length > 0 && (
        <Sec title="기본 정보">
          {info.map(x => <div key={x} style={{ ...ROW.body, padding: '10px 0', ...clamp(1) }}>{x}</div>)}
        </Sec>
      )}
      <SaveBar side={16}>
        <button type="button" className="tds-btn" onClick={goMemo}
          style={{ width: '100%', height: 56, borderRadius: 16, border: 'none', background: NAVY, color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>메모 남기기</button>
      </SaveBar>
      {sheetTask && <MobileTaskSheet key={sheetTask.id} task={sheetTask} comp={c} run={run} onClose={() => setSheetId('')} />}
      {undoNode}
    </div>
  );
}

/** 팀장님 한눈에 */
export function FpView({ f, companies, tasks, consultations, onCompany, saveMemo, goCompany, goConsult }: {
  f: FP; companies: Company[]; tasks: Task[]; consultations: Consultation[];
  onCompany: (id: string) => void; saveMemo: (text: string) => Promise<boolean>; goCompany: () => void; goConsult: () => void;
}) {
  const mine = useMemo(() => companies.filter(c => (c.fps || []).some(x => x.fp_id === f.id))
    .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''))), [companies, f.id]);
  const rows = useMemo(() => activeRows(tasks.filter(t => mine.some(c => c.id === t.company_id)), mine), [tasks, mine]);
  const { next, past } = useMemo(() => consultBuckets(consultations.filter(x => x.fp_id === f.id)), [consultations, f.id]);
  const [memos, setMemos] = useState<Memo[] | null>(null);
  // 소개 업체 메모 — 최근 손댄 8곳만(전화 중 기다리지 않게)
  useEffect(() => {
    let live = true;
    setMemos(null);
    const top = mine.slice(0, 8);
    Promise.all(top.map(c => api.getComments(c.id).then(r => (r.data || []).map(x => toMemo(x, c.name))).catch(() => [] as Memo[])))
      .then(all => { if (live) setMemos(all.flat().sort((a, b) => b.at.localeCompare(a.at))); });
    return () => { live = false; };
  }, [mine]);
  const [writing, setWriting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [pLim, pMore] = useMore(3);
  const [mLim, mMore] = useMore(5);
  const [cLim, cMore] = useMore(8);
  const fpMemo = String(f.memo || '').trim();
  const line = headLine(['업체', mine.length], rows, next[0]);
  const idle = mine.filter(c => !rows.some(x => x.r.comp.id === c.id));   // 진행 업무 줄에 이미 나온 업체는 빼고(같은 4곳 두 번 — 패널)
  const coLine = (c: Company) => { const r = rows.filter(x => x.r.comp.id === c.id); return r.length ? { text: `${r[0].r.step ? r[0].r.step.name : r[0].r.task.type} · ${r[0].st.text}`, color: r[0].st.color } : { text: c.tm_status, color: G.g700 }; };
  const save = async () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    const okd = await saveMemo(text.trim());
    setBusy(false);
    if (okd) { setText(''); setWriting(false); }
  };
  return (
    <div>
      <Summary kind="fp" title={fpLabel(f)} chip={fpOrg(f) ? short(fpOrg(f), 10) : undefined} line={line} />
      {f.phone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, margin: '0 0 12px', padding: '6px 20px', borderRadius: 20, background: '#fff', border: `1px solid ${TOSS.line}`, fontSize: 17, color: TOSS.text, fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', ...clamp(1) }}><b style={{ fontWeight: 700 }}>팀장님</b> {f.phone}</span>
            {f.wm_name && <span style={{ display: 'block', color: TOSS.sub, fontSize: 15, ...clamp(1) }}>WM {f.wm_name}</span>}
          </span>
          <button type="button" className="tds-btn" onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(f.phone).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); }).catch(() => undefined); }}
            style={{ flexShrink: 0, height: 44, minWidth: 64, padding: '0 12px', borderRadius: 12, border: 'none', background: TOSS.blueLt, color: TOSS.blueDk, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{copied ? '복사됨' : '복사'}</button>
        </div>
      )}

      <Sec title="마지막 메모">
        {fpMemo ? <div style={{ fontSize: 16, color: G.g900, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...clamp(3) }}>{fpMemo}</div>
          : memos === null ? <Empty>불러오는 중이에요</Empty> : memos.length ? <MemoRows list={memos.slice(0, 1)} /> : <Empty>남긴 메모가 없어요</Empty>}
      </Sec>
      <Sec title="진행 업무" count={rows.length}>
        {rows.length ? <TaskRows rows={rows.slice(0, 8)} showCompany onCompany={onCompany} /> : <Empty>진행 중인 업무가 없어요</Empty>}
      </Sec>
      <Sec title="상담 이력" count={next.length + past.length} right={<LinkBtn onClick={goConsult}>＋ 상담 등록</LinkBtn>}>
        {next.length + past.length === 0 && <Empty>동행 기록이 없어요</Empty>}
        <ConsultRows list={[...next, ...past.slice(0, pLim)]} who="fp" onCompany={onCompany} />
        {pMore(past.length)}
      </Sec>
      <Sec title="진행 업무 없는 업체" count={idle.length} right={<LinkBtn onClick={goCompany}>＋ 업체 등록</LinkBtn>}>
        {idle.length === 0 && <Empty>{mine.length ? '소개 업체 모두 진행 중이에요' : '소개한 업체가 없어요'}</Empty>}
        {idle.slice(0, cLim).map(c => { const l = coLine(c); return (
          <button key={c.id} type="button" className="tds-row" onClick={() => onCompany(c.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 56, padding: '6px 0', border: 'none', borderBottom: `1px solid ${G.g100}`, background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: G.g900, ...clamp(1) }}>{c.name}</span>
              <span style={{ display: 'block', fontSize: 14, color: l.color, ...clamp(1) }}>{l.text}</span>
            </span>
            <Icon name="r" size={18} color={G.g500} sw={2} />
          </button>
        ); })}
        {cMore(idle.length)}
      </Sec>
      <Sec title="업체 메모" count={memos ? memos.length : undefined}>
        {memos === null && <Empty>불러오는 중이에요</Empty>}
        {memos && memos.length === 0 && <Empty>남긴 메모가 없어요</Empty>}
        {memos && <MemoRows list={memos.slice(0, mLim)} />}
        {memos && mMore(memos.length)}
      </Sec>
      <SaveBar side={16}>
        {writing ? (
          <div>
            <textarea className="tds-in" value={text} onChange={e => setText(e.target.value)} rows={3} autoFocus aria-label="팀장님 메모" placeholder="통화 내용 · 부탁받은 것"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 14, border: `1px solid ${G.g500}`, fontSize: 17, lineHeight: 1.5, boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit', outline: 'none' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 8, marginTop: 8 }}>
              <button type="button" className="tds-btn" onClick={() => { setWriting(false); setText(''); }} style={{ height: 52, borderRadius: 14, border: `1px solid ${G.g300}`, background: '#fff', color: G.g800, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>닫기</button>
              <button type="button" className="tds-btn" onClick={() => void save()} aria-disabled={!text.trim() || busy}
                style={{ height: 52, borderRadius: 14, border: 'none', background: text.trim() && !busy ? NAVY : G.g200, color: text.trim() && !busy ? '#fff' : G.g500, fontSize: 17, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{busy ? '저장 중' : '저장'}</button>
            </div>
          </div>
        ) : (
          <button type="button" className="tds-btn" onClick={() => setWriting(true)}
            style={{ width: '100%', height: 56, borderRadius: 16, border: 'none', background: NAVY, color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>메모 남기기</button>
        )}
      </SaveBar>
    </div>
  );
}

/* ── 연락처 내려받기(vCard) — 전화가 오면 휴대폰 화면에 「김○○ 팀장 · 한화 ○○지점」이 뜨게 ── */
const vEsc = (s: string) => String(s || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
/** origin 을 주면 연락처마다 그 사람 · 업체 한눈에 주소(/m?fp= · /m?co=)를 넣는다 — 연락처에서 주소만 누르면 바로 열림(패널) */
export function vcardOf(fps: FP[], companies: Company[], withCeo: boolean, origin = ''): { text: string; count: number; keys: string[] } {
  const cards: string[] = [];
  const keys: string[] = [];
  const seen = new Set<string>();
  const card = (key: string, name: string, org: string, phone: string, url: string) => {
    const d = digits(phone);
    if (d.length < 9 || seen.has(d)) return;
    seen.add(d); keys.push(key);
    cards.push(['BEGIN:VCARD', 'VERSION:3.0', `FN:${vEsc(name)}`, `N:${vEsc(name)};;;;`, org ? `ORG:${vEsc(org)}` : '', `TEL;TYPE=CELL:${d}`,
      origin ? `URL:${origin}${url}` : '', 'NOTE:로움 업무관리에서 내려받음', 'END:VCARD'].filter(Boolean).join('\r\n'));
  };
  fps.filter(f => f.is_active !== false).forEach(f => card(`fp:${f.id}`, `${fpLabel(f)}${fpOrg(f) ? ` · ${fpOrg(f)}` : ''} [로움]`, fpOrg(f), f.phone, `/m?fp=${f.id}`));
  if (withCeo) companies.forEach(c => {
    if (c.ceo) card(`co:${c.id}`, `${c.ceo} 대표 · ${c.name} [로움]`, c.name, c.ceo_phone, `/m?co=${c.id}`);
    if (c.real_ceo && c.real_ceo_phone) card(`co-real:${c.id}`, `${c.real_ceo} 실제 대표 · ${c.name} [로움]`, c.name, c.real_ceo_phone, `/m?co=${c.id}`);   // 실제 대표(2026-10-18)
  });
  return { text: cards.join('\r\n') + '\r\n', count: cards.length, keys };
}
const LAST_VCF = 'roum-m-vcf';
function readLastVcf(): { at: string; keys: string[] } | null { try { const v = JSON.parse(localStorage.getItem(LAST_VCF) || 'null') as { at: string; keys: string[] } | null; return v && Array.isArray(v.keys) ? v : null; } catch { return null; } }

/** 연락처 내려받기 화면 — .vcf 한 파일(휴대폰이 「연락처에 추가」로 엶) */
export function ContactsView({ fps, companies }: { fps: FP[]; companies: Company[] }) {
  const [withCeo, setWithCeo] = useState(true);   // 업체에서 온 전화도 알아보게(패널) — 기본 = 팀장님 + 업체 대표
  const [done, setDone] = useState(false);
  const [last, setLast] = useState(() => readLastVcf());
  const v = useMemo(() => vcardOf(fps, companies, withCeo, typeof window !== 'undefined' ? window.location.origin : ''), [fps, companies, withCeo]);
  const fresh = last ? v.keys.filter(k => !last.keys.includes(k)).length : 0;
  const down = () => {
    if (!v.count) return;
    const url = URL.createObjectURL(new Blob([v.text], { type: 'text/vcard;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `roum_contacts_${kstToday()}.vcf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    setDone(true);
    const rec = { at: kstToday(), keys: v.keys };
    try { localStorage.setItem(LAST_VCF, JSON.stringify(rec)); } catch { /* 보관 못 함 */ }
    setLast(rec);
  };
  const p: CSSProperties = { fontSize: 16, color: G.g800, lineHeight: 1.65, margin: '10px 0 0', wordBreak: 'keep-all' };
  return (
    <div>
      <p style={p}>전화가 오면 휴대폰 화면에 <b>「김○○ 팀장 · 한화생명 ○○지점 [로움]」</b>처럼 떠요.</p>
      <div style={{ marginTop: 16 }}><Chips cols={2} items={[{ k: 'all', t: '팀장님 + 업체 대표' }, { k: 'fp', t: '팀장님만' }]} value={[withCeo ? 'all' : 'fp']} onChange={x => { setWithCeo(x[0] === 'all'); setDone(false); }} /></div>
      {last && <p style={{ ...p, fontSize: 15, color: G.g700 }}>{md(last.at)}에 받음{fresh ? <> · 그 뒤 새로 생긴 <b style={{ color: G.g900 }}>{fresh}명</b></> : ' · 그 뒤 새로 생긴 분 없음'}</p>}
      <p style={{ ...p, fontSize: 15, color: G.g700 }}>연락처의 주소를 누르면 그분 화면이 바로 열려요. 이미 저장한 분은 겹칠 수 있어요.</p>
      {done && <p style={{ ...p, color: '#05864F', fontWeight: 700 }}>받았어요 · 파일을 열어 「연락처에 추가」를 누르세요</p>}
      <SaveBar>
        <button type="button" className="tds-btn" onClick={down} aria-disabled={!v.count}
          style={{ width: '100%', height: 56, borderRadius: 16, border: 'none', background: v.count ? NAVY : G.g200, color: v.count ? '#fff' : G.g500, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {v.count ? `연락처 ${v.count.toLocaleString('ko-KR')}명 내려받기` : '넣을 번호가 없어요'}
        </button>
      </SaveBar>
    </div>
  );
}

/** 고객 업무 판(정식 업무 · 돈 받고 하는 일 — 일일 업무와 따로) — 기한 지남 · 오늘 마감 · 이번 주(7일 안) · 회신 대기
 *  (2026-10-12 지도사 「일일 업무는 잔 업무를 수시로 등록 · 해결 / 정식 업무는 대부분 돈 받고 하는 업무 — 기한이 다가오는 것과 다르다」) */
export type BoardTab = 'over' | 'today' | 'week' | 'wait';
export const BOARD_TABS: { k: BoardTab; t: string }[] = [{ k: 'over', t: '기한 지남' }, { k: 'today', t: '오늘 마감' }, { k: 'week', t: '이번 주' }, { k: 'wait', t: '회신 대기' }];
export function boardRows(tasks: Task[], companies: Company[]): Record<BoardTab, { r: FlowRow; st: TaskState }[]> {
  const all = activeRows(tasks, companies);
  return {
    over: all.filter(x => x.st.over),
    today: all.filter(x => x.r.kind === 'mine' && x.r.d === 0),
    week: all.filter(x => x.r.kind === 'mine' && x.r.d !== null && x.r.d >= 1 && x.r.d <= 7),
    wait: all.filter(x => x.r.kind === 'wait'),
  };
}
export function BoardView({ tasks, companies, tab0, onCompany }: { tasks: Task[]; companies: Company[]; tab0: BoardTab; onCompany: (id: string) => void }) {
  const [tab, setTab] = useState<BoardTab>(tab0);
  const b = useMemo(() => boardRows(tasks, companies), [tasks, companies]);
  const rows = b[tab];
  return (
    <div style={{ paddingTop: 4 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 4, padding: 4, borderRadius: 14, background: TOSS.line, marginBottom: 12 }}>
        {BOARD_TABS.map(x => (
          <button key={x.k} type="button" className="tds-btn" onClick={() => setTab(x.k)} aria-pressed={tab === x.k}
            style={{ height: 52, borderRadius: 10, border: 'none', background: tab === x.k ? '#fff' : 'transparent', boxShadow: tab === x.k ? '0 1px 3px rgba(0,0,0,.12)' : 'none', color: tab === x.k ? TOSS.text : TOSS.sub, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.2 }}>
            {x.t}<span style={{ display: 'block', fontSize: 15, color: x.k === 'over' && b.over.length ? TOSS.red : 'inherit', fontVariantNumeric: 'tabular-nums' }}>{b[x.k].length}</span>
          </button>
        ))}
      </div>
      {rows.length === 0 ? <Card pad="20px"><div style={{ fontSize: 16, color: TOSS.sub }}>없어요</div></Card>
        : <Card pad="4px 20px 6px"><TaskRows rows={rows} showCompany onCompany={onCompany} /></Card>}
    </div>
  );
}
